import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { TackLogo } from "@/components/TackLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { acceptCashierInvite } from "@/lib/staff.functions";
import { withAuth } from "@/lib/server-fn-auth";

export const Route = createFileRoute("/accept-invite")({
  component: AcceptInvitePage,
});

type ExchangeState = "pending" | "ready" | "error";

function AcceptInvitePage() {
  const { isAuthenticated, isLoading, updatePassword } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [exchangeState, setExchangeState] = useState<ExchangeState>("pending");
  const [exchangeError, setExchangeError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;

    async function establishSession() {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        const tokenHash = url.searchParams.get("token_hash");
        const type = url.searchParams.get("type");
        const hash = window.location.hash;

        const { data: existing } = await supabase.auth.getSession();
        if (existing.session || (!isLoading && isAuthenticated)) {
          if (!cancelled) setExchangeState("ready");
          return;
        }

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else if (tokenHash && (type === "invite" || type === "email")) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type === "invite" ? "invite" : "email",
          });
          if (error) throw error;
        } else if (hash.includes("access_token")) {
          await new Promise((r) => setTimeout(r, 200));
          const { data: retry } = await supabase.auth.getSession();
          if (!retry.session) throw new Error(t('auth.inviteVerifyFailed'));
        } else {
          throw new Error(t('auth.inviteInvalid'));
        }

        window.history.replaceState({}, "", "/accept-invite");
        if (!cancelled) setExchangeState("ready");
      } catch (err: any) {
        console.error("Accept invite session exchange failed:", err);
        if (!cancelled) {
          setExchangeError(err?.message || t('auth.inviteExpired'));
          setExchangeState("error");
        }
      }
    }

    establishSession();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, t]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t('auth.passwordMin6'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsDontMatch'));
      return;
    }
    setSubmitting(true);
    try {
      await updatePassword(password);
      await withAuth(acceptCashierInvite)();
      toast.success(t('auth.allSetWelcome'));
      navigate({ to: "/cashier" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('auth.couldNotSetPassword'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading || exchangeState === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t('auth.loadingDots')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <TackLogo size="lg" />
        </div>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="font-display">{t('auth.setYourPassword')}</CardTitle>
            <CardDescription>{t('auth.inviteDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {exchangeState === "error" ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-destructive">{exchangeError}</p>
                <Button variant="outline" className="w-full" onClick={() => navigate({ to: "/login" })}>
                  {t('auth.backToLoginBtn')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.newPasswordLabel')}</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">{t('auth.confirmPasswordLabel')}</Label>
                  <Input id="confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? t('auth.saving') : t('auth.activateCashier')}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
